/*
 * 模板 vs 已填样例的 Excel 结构化 diff（纯前端，不上传样例）
 *
 * 用法：用户在 Home 页同时选中"模板"和"参考样例"，前端用 ExcelJS 解析两份
 * .xlsx，比较每个 sheet 每个 cell。结果作为首条消息的引文注入给 agent，
 * agent 据此模仿样例的字段映射来填模板。样例文件本身永远不离开浏览器。
 *
 * 不做的事：
 *   - 公式比较（只看显示值）
 *   - 样式 / 合并单元格的语义对齐（仅按 ref 一一对照）
 *   - 工作表名映射（两边 sheet 名不一致时按位置对齐）
 */

import ExcelJS from 'exceljs'
import JSZip from 'jszip'

export interface ExcelDiffFilledCell {
  sheet: string
  ref: string
  header?: string
  value: string
  /** 该 cell 是合并区域的主单元格时，记录区域字符串（如 "A4:A12"）。
   *  prompt 输出会标成 `A(合并 A4:A12)=1`，让 agent 看到结构化合并信号 */
  mergedRange?: string
}

export interface ExcelDiffDifferingCell {
  sheet: string
  ref: string
  header?: string
  templateValue: string
  sampleValue: string
  /** 若该 cell 是合并区域的主单元格，记录区域字符串（如 "A1:Z1"），输出时合并成一行 */
  mergedRange?: string
}

export interface ExcelDiffSheetDims {
  rows: number
  cols: number
}

export interface ExcelDiffImageCell {
  sheet: string
  ref: string
  header?: string
  /** cell 里的占位文本（如"照片"），仅作上下文，agent 不应直接当文本填 */
  placeholderText?: string
}

export interface ExcelDiff {
  templateName: string
  sampleName: string
  /** 模板里空着、样例里填了文本/数值 → 字段映射证据 */
  filledByExample: ExcelDiffFilledCell[]
  /** 样例里这个 cell 上盖了图片（drawing 层 anchor），文本只是占位
   *  — agent 看到这类必须插图片，不能直接填文本 */
  imageCells: ExcelDiffImageCell[]
  /** 两边都填了，但值不同 → 提示用户/agent 这里有冲突 */
  differing: ExcelDiffDifferingCell[]
  /** 样例覆盖到的 sheet 列表 */
  sheets: string[]
  /** 每个 sheet 的尺寸（rows × cols） */
  dims: Record<string, ExcelDiffSheetDims>
  /** 每个 sheet 自动识别出的"真表头"行号（1-based）。某些 xlsx 上面有合并标题行，
   *  真表头在第 3 行；老逻辑硬取第 1 行会拿到无意义的标题文本污染每条字段。 */
  headerRows: Record<string, number>
  /** 每个 sheet 的列字母 → 表头文本映射，从 headerRows[sheet] 行提取，
   *  diffToPromptText 用它统一在顶部列一次表头，不再每条字段重复粘贴。 */
  headers: Record<string, Record<string, string>>
}

/**
 * 识别"占位文本"——模板预填的 placeholder（如"请输入内容..."），不是真实数据。
 * 这种文本如果当作 sample 的填写示例传给 agent，会让它把"请输入内容"当真值填，
 * 是真实出现过的语义错误。所以扫描时统一识别 + 视为空 cell 处理。
 *
 * 命中标准：
 *   - 中文常见占位短语（请输入/请填写/请选择/点击填写/待填/待补充）
 *   - 占位符号本身（"…" / "..." / 全空白）
 *   - 括号包占位（如"（请输入）" / "[待填]"）
 */
function isPlaceholderText(text: string): boolean {
  if (!text) return true
  const trimmed = text.trim()
  if (!trimmed) return true
  // 占位符号本身
  if (/^[\s.…]+$/.test(trimmed)) return true
  // 中文占位短语
  if (/请(输入|填写|选择|完善|补充|添加)/.test(trimmed)) return true
  if (/(待填|待补充|待录入|待选|待完善)/.test(trimmed)) return true
  if (/^点击(填写|选择|添加|输入)/.test(trimmed)) return true
  // 括号包的占位（"（待填）" / "[请输入]"）
  if (
    /^[（([【].{0,12}(待填|请输入|请填写|空|无).{0,4}[)）\]】]$/.test(trimmed)
  )
    return true
  return false
}

/** value 输出截断阈值。超长 cell 单独截断，避免 prompt 爆炸。 */
const MAX_VALUE_OUTPUT_LEN = 200

/** 把 cell value 限制在合理长度内，超长截断并保留首尾。换行折成单行。 */
function truncateValue(text: string): string {
  // newline → 单空格折叠（让 cell 单行，不破坏 r4: A=... | B=... 格式）
  let s = text.replace(/[\r\n]+/g, ' ↵ ')
  if (s.length > MAX_VALUE_OUTPUT_LEN) {
    const keep = MAX_VALUE_OUTPUT_LEN - 30
    const head = s.slice(0, keep)
    s = `${head}…(原文 ${text.length} 字省略)`
  }
  return s
}

/** ExcelJS cell value → 显示用字符串。空 cell 返回 ''。 */
function cellToText(cell: ExcelJS.Cell | undefined): string {
  if (!cell) return ''
  const v = cell.value
  if (v == null) return ''
  if (typeof v === 'string') return v.trim()
  if (typeof v === 'number') return String(v)
  if (typeof v === 'boolean') return v ? 'TRUE' : 'FALSE'
  if (v instanceof Date) {
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${v.getFullYear()}-${pad(v.getMonth() + 1)}-${pad(v.getDate())}`
  }
  // RichText
  if (typeof v === 'object' && 'richText' in v && Array.isArray(v.richText)) {
    return v.richText
      .map((t) => t.text)
      .join('')
      .trim()
  }
  // Hyperlink / formula → 用 result 兜底
  if (
    typeof v === 'object' &&
    'text' in v &&
    typeof (v as any).text === 'string'
  ) {
    return (v as any).text.trim()
  }
  if (typeof v === 'object' && 'result' in v) {
    const r = (v as any).result
    return r == null ? '' : String(r).trim()
  }
  return String(v).trim()
}

/**
 * 自动识别"真表头"行号。xlsx 顶部经常有合并标题行（"照片台账表 日期范围："）+
 * 副信息行（"报表生成时间："），真正的表头在 r3。老 pickHeader 硬取 r1 会让每条
 * 字段都贴上一段 60 字的合并标题，污染 prompt。
 *
 * 启发式：扫前 10 行，每行打分 = 非空 + 不是合并占位 + 短文本 (<=30) + 值唯一。
 *   分数最高的行视为表头；并列时取行号小的；都 0 分则 fallback 到 1。
 */
function detectHeaderRow(
  sheet: ExcelJS.Worksheet,
  mergedSlaves: Set<string>
): number {
  const maxRow = Math.min(sheet.rowCount || 0, 10) || 0
  if (maxRow === 0) return 1
  const maxCol = Math.min(sheet.columnCount || 0, 50) || 0
  let bestRow = 1
  let bestScore = -1
  for (let r = 1; r <= maxRow; r++) {
    let score = 0
    const seen = new Set<string>()
    for (let c = 1; c <= maxCol; c++) {
      const colLetter = numberToColLetter(c)
      if (mergedSlaves.has(`${colLetter}${r}`)) continue
      const cell = sheet.getCell(r, c)
      const text = cellToText(cell)
      if (!text) continue
      if (text.length > 30) {
        score -= 1
        continue
      }
      if (seen.has(text)) continue // 同行重复值不像表头
      seen.add(text)
      score += 2
    }
    if (score > bestScore) {
      bestScore = score
      bestRow = r
    }
  }
  return bestRow
}

/**
 * 把表头行抽成 { A: '照片/视频', B: '拍摄人', ... } 映射。空 cell 不入。
 * 用作 prompt 顶部统一列表头，减去每条字段重复粘贴的开销。
 */
function extractHeaders(
  sheet: ExcelJS.Worksheet,
  headerRow: number,
  mergedSlaves: Set<string>
): Record<string, string> {
  const out: Record<string, string> = {}
  const maxCol = Math.min(sheet.columnCount || 0, 200) || 0
  for (let c = 1; c <= maxCol; c++) {
    const colLetter = numberToColLetter(c)
    if (mergedSlaves.has(`${colLetter}${headerRow}`)) continue
    const text = cellToText(sheet.getCell(headerRow, c))
    if (!text) continue
    out[colLetter] = text
  }
  return out
}

/**
 * 解析合并区域字符串 "A1:Z1" → { master: 'A1', slaves: ['B1', 'C1', ..., 'Z1'], range }。
 * cell-scan 时跳过 slaves（防止 26 个 cell 同值刷屏）；output 时把 differing 的
 * master 标上 mergedRange 让上层把 "A1:Z1: 模板=... → 样例=..." 合并成一行。
 */
function parseMergeRange(
  range: string
): { master: string; slaves: string[]; range: string } | null {
  const m = range.match(/^([A-Z]+)(\d+):([A-Z]+)(\d+)$/)
  if (!m) return null
  const c1 = colLetterToNumber(m[1])
  const r1 = parseInt(m[2], 10)
  const c2 = colLetterToNumber(m[3])
  const r2 = parseInt(m[4], 10)
  if (!Number.isFinite(c1) || !Number.isFinite(c2)) return null
  const cMin = Math.min(c1, c2)
  const cMax = Math.max(c1, c2)
  const rMin = Math.min(r1, r2)
  const rMax = Math.max(r1, r2)
  const master = `${numberToColLetter(cMin)}${rMin}`
  const slaves: string[] = []
  for (let r = rMin; r <= rMax; r++) {
    for (let c = cMin; c <= cMax; c++) {
      const ref = `${numberToColLetter(c)}${r}`
      if (ref !== master) slaves.push(ref)
    }
  }
  return {
    master,
    slaves,
    range: `${numberToColLetter(cMin)}${rMin}:${numberToColLetter(cMax)}${rMax}`
  }
}

/**
 * 收集 sheet 所有合并区域，返回：
 *   - slaves: 所有非主格的 cell ref 集合（cell-scan 时直接跳过）
 *   - rangeOfMaster: 主格 → 区域字符串（让 differing 输出可以合并多个 cell 成一条）
 */
function collectMerges(sheet: ExcelJS.Worksheet): {
  slaves: Set<string>
  rangeOfMaster: Map<string, string>
} {
  const slaves = new Set<string>()
  const rangeOfMaster = new Map<string, string>()
  // ExcelJS 把 merges 暴露在 model.merges (string[]) 或 _merges。两个都试。
  const sm: any = sheet
  const merges: string[] =
    (sm.model && Array.isArray(sm.model.merges) && sm.model.merges) ||
    (sm._merges && typeof sm._merges === 'object'
      ? Object.keys(sm._merges)
      : [])
  for (const range of merges) {
    const parsed = parseMergeRange(range)
    if (!parsed) continue
    parsed.slaves.forEach((ref) => slaves.add(ref))
    rangeOfMaster.set(parsed.master, parsed.range)
  }
  return { slaves, rangeOfMaster }
}

/** 按 col 字母（如表头行）取该 sheet 表头文本，找不到返回 undefined */
function pickHeader(
  sheet: ExcelJS.Worksheet,
  colLetter: string,
  headerRow: number
): string | undefined {
  if (headerRow <= 0) return undefined
  const cell = sheet.getRow(headerRow).getCell(colLetter)
  const text = cellToText(cell)
  return text || undefined
}

/**
 * 扫 drawing 层，找出每个 sheet 上盖了图片的 cell ref。必须在 stripSheetDrawings
 * 之前调用，因为 strip 之后 drawing 引用就没了。
 *
 * 返回：sheet 索引（0-based，对应 ExcelJS workbook.worksheets[i]）→ Set<cellRef>
 *   例如 { 0 → Set { 'A4', 'A5', 'A6' } }
 *
 * 失败（DOMParser 异常 / 找不到关键文件）→ 返回空 Map，diff 还是能算，只是丢了
 * 图片提示。
 */
async function detectImageCells(
  buf: ArrayBuffer
): Promise<Map<number, Set<string>>> {
  const result = new Map<number, Set<string>>()
  try {
    const zip = await JSZip.loadAsync(buf)
    const parser = new DOMParser()

    // workbook.xml.rels: rId → sheet target（如 "worksheets/sheet1.xml"）
    const wbRelsXml = await zip
      .file('xl/_rels/workbook.xml.rels')
      ?.async('string')
    if (!wbRelsXml) return result
    const wbRelsDoc = parser.parseFromString(wbRelsXml, 'application/xml')
    const wbRelMap = new Map<string, string>()
    Array.from(wbRelsDoc.getElementsByTagName('Relationship')).forEach((r) => {
      const id = r.getAttribute('Id') || ''
      const target = r.getAttribute('Target') || ''
      if (id) wbRelMap.set(id, target)
    })

    // workbook.xml 里 <sheet> 出现顺序就是 ExcelJS worksheets[i] 顺序
    const wbXml = await zip.file('xl/workbook.xml')?.async('string')
    if (!wbXml) return result
    const wbDoc = parser.parseFromString(wbXml, 'application/xml')
    const sheetEls = Array.from(wbDoc.getElementsByTagName('sheet'))

    for (let i = 0; i < sheetEls.length; i++) {
      // 不同写出器 r:id 可能是 "r:id" 或带其他前缀，DOMParser 会把它放到 attributes
      const sheetEl = sheetEls[i]
      let rid = ''
      for (const attr of Array.from(sheetEl.attributes)) {
        if (
          attr.localName === 'id' &&
          /(^|:)id$/.test(attr.name) &&
          attr.name !== 'sheetId'
        ) {
          rid = attr.value
          break
        }
      }
      if (!rid) continue
      const sheetTarget = wbRelMap.get(rid)
      if (!sheetTarget) continue

      // sheetTarget = "worksheets/sheet1.xml"，绝对路径在 xl/ 下
      const sheetPath = sheetTarget.startsWith('/')
        ? sheetTarget.slice(1)
        : `xl/${sheetTarget}`
      const sheetDir = sheetPath.replace(/[^/]+$/, '') // "xl/worksheets/"
      const sheetFile = sheetPath.split('/').pop() || ''
      const sheetRelsPath = `${sheetDir}_rels/${sheetFile}.rels`

      const sheetRelsXml = await zip.file(sheetRelsPath)?.async('string')
      if (!sheetRelsXml) continue
      const sheetRelsDoc = parser.parseFromString(
        sheetRelsXml,
        'application/xml'
      )

      let drawingTarget = ''
      for (const r of Array.from(
        sheetRelsDoc.getElementsByTagName('Relationship')
      )) {
        if ((r.getAttribute('Type') || '').includes('/drawing')) {
          drawingTarget = r.getAttribute('Target') || ''
          break
        }
      }
      if (!drawingTarget) continue

      // drawingTarget 形如 "../drawings/drawing1.xml"，相对 sheetDir
      const drawingPath = resolveRelativePath(sheetDir, drawingTarget)
      const drawingXml = await zip.file(drawingPath)?.async('string')
      if (!drawingXml) continue

      const cells = new Set<string>()
      // 找每个 anchor 的 <xdr:from>: col / row 都是 0-indexed
      const anchorRe =
        /<xdr:from>[\s\S]*?<xdr:col>(\d+)<\/xdr:col>[\s\S]*?<xdr:row>(\d+)<\/xdr:row>[\s\S]*?<\/xdr:from>/g
      let m: RegExpExecArray | null
      while ((m = anchorRe.exec(drawingXml))) {
        const col = parseInt(m[1], 10)
        const row = parseInt(m[2], 10)
        if (!Number.isFinite(col) || !Number.isFinite(row)) continue
        cells.add(`${numberToColLetter(col + 1)}${row + 1}`)
      }
      if (cells.size > 0) result.set(i, cells)
    }
  } catch {
    // ignore
  }
  return result
}

/** 解析相对路径：base="xl/worksheets/" + rel="../drawings/drawing1.xml" → "xl/drawings/drawing1.xml" */
function resolveRelativePath(base: string, rel: string): string {
  const stack = base.split('/').filter(Boolean)
  rel.split('/').forEach((p) => {
    if (p === '..') stack.pop()
    else if (p && p !== '.') stack.push(p)
  })
  return stack.join('/')
}

/**
 * ExcelJS load 前的 ZIP 预处理：剥掉所有 sheet 的 <drawing> 引用。
 *
 * 背景：部分 xlsx 写出时 <drawing> 元素用了非标准命名空间前缀（写成
 * `relationships:id="rId1"` 而不是 `r:id="rId1"`），ExcelJS 解析时拿不到
 * rId，reconcile 阶段读 `rels[undefined].Target` 直接抛
 * `Cannot read properties of undefined (reading 'Target')`。
 *
 * diff 不依赖 drawing/image，安全做法：load 前直接把 sheet xml 里的
 * <drawing/> 元素整体删掉。失败就退回原 buffer，让 ExcelJS 自己尝试。
 */
async function stripSheetDrawings(buf: ArrayBuffer): Promise<ArrayBuffer> {
  try {
    const zip = await JSZip.loadAsync(buf)
    const sheetPaths = Object.keys(zip.files).filter((p) =>
      /^xl\/worksheets\/sheet[^/]+\.xml$/.test(p)
    )
    let touched = false
    for (const p of sheetPaths) {
      const file = zip.file(p)
      if (!file) continue
      const xml = await file.async('string')
      const cleaned = xml
        .replace(/<drawing\b[^>]*\/>/g, '')
        .replace(/<drawing\b[^>]*>[\s\S]*?<\/drawing>/g, '')
      if (cleaned !== xml) {
        zip.file(p, cleaned)
        touched = true
      }
    }
    if (!touched) return buf
    return (await zip.generateAsync({ type: 'arraybuffer' })) as ArrayBuffer
  } catch {
    return buf
  }
}

async function loadWorkbook(buf: ArrayBuffer): Promise<ExcelJS.Workbook> {
  const cleaned = await stripSheetDrawings(buf)
  const wb = new ExcelJS.Workbook()
  await wb.xlsx.load(cleaned)
  return wb
}

// ───────────── 预匹配探测：home 页选完两份文件后立刻跑 ─────────────
//
// 目的：在用户跳转 work 页之前就告诉 ta "样例和模板长得像不像"，避免上传完跑半天
// 才发现样例字段对不上的高时间成本错误。
//
// 探测维度：
//   1. matchPercent: 表头列字母 + 表头文本相似度（同名/含义相近列 / 总列数）
//   2. reversed: 模板填得比样例还满（counterpart 比 sample 填了更多 cell）
//   3. parseError: 解析失败时返回错误，不阻断用户上传
//
// 性能：完整跑一遍 detectHeaderRow + extractHeaders（≈ ExcelJS load + 几次 sheet
//       header 扫描），实测 < 1s/份。比 computeExcelDiff 便宜（不扫数据 cell）。

export interface MatchProbeResult {
  matchPercent: number
  matchedHeaders: number
  totalHeaders: number
  reversed: boolean
  templateFilledCount: number
  sampleFilledCount: number
  parseError?: string
}

/** 把表头文本归一化用作模糊匹配：trim、小写、去括号注释、去 markdown */
function normalizeHeaderForMatch(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .replace(/[(（].*?[)）]/g, '')
    .replace(/[*_`#~]/g, '')
    .replace(/\s+/g, '')
}

/** 两个表头是否"相似"：完全相等 / 归一化后相等 / 一方包含另一方 */
function isHeaderSimilar(a: string, b: string): boolean {
  if (!a || !b) return false
  if (a === b) return true
  const na = normalizeHeaderForMatch(a)
  const nb = normalizeHeaderForMatch(b)
  if (!na || !nb) return false
  if (na === nb) return true
  if (na.length >= 2 && nb.length >= 2) {
    if (na.includes(nb) || nb.includes(na)) return true
  }
  return false
}

/** 数 sheet 内非空 cell 的数量（用于 reversed 检测；只看头 200 行避免大表慢） */
function countFilledCells(sheet: ExcelJS.Worksheet | undefined): number {
  if (!sheet) return 0
  let count = 0
  const maxRow = Math.min(sheet.rowCount || 0, 200)
  const maxCol = Math.min(sheet.columnCount || 0, 100)
  for (let r = 1; r <= maxRow; r++) {
    for (let c = 1; c <= maxCol; c++) {
      const text = cellToText(sheet.getCell(r, c))
      if (text && !isPlaceholderText(text)) count++
    }
  }
  return count
}

export async function probeTemplateSampleMatch(
  templateBuf: ArrayBuffer,
  sampleBuf: ArrayBuffer
): Promise<MatchProbeResult> {
  let templateWb: ExcelJS.Workbook
  let sampleWb: ExcelJS.Workbook
  try {
    ;[templateWb, sampleWb] = await Promise.all([
      loadWorkbook(templateBuf),
      loadWorkbook(sampleBuf)
    ])
  } catch (e) {
    return {
      matchPercent: 0,
      matchedHeaders: 0,
      totalHeaders: 0,
      reversed: false,
      templateFilledCount: 0,
      sampleFilledCount: 0,
      parseError: e instanceof Error ? e.message : String(e)
    }
  }

  // sheet 配对（同 computeExcelDiff 的策略：先 name 匹配再位置 fallback）
  const tSheetByName = new Map<string, ExcelJS.Worksheet>()
  templateWb.worksheets.forEach((s) => tSheetByName.set(s.name, s))

  let totalMatched = 0
  let totalHeaders = 0
  let templateFilled = 0
  let sampleFilled = 0

  for (let si = 0; si < sampleWb.worksheets.length; si++) {
    const sSheet = sampleWb.worksheets[si]
    if (!sSheet) continue
    const tSheet =
      tSheetByName.get(sSheet.name) || templateWb.worksheets[si] || undefined

    // 表头匹配
    const sMerges = collectMerges(sSheet)
    const sHeaderRow = detectHeaderRow(sSheet, sMerges.slaves)
    const sHeaders = extractHeaders(sSheet, sHeaderRow, sMerges.slaves)
    const sCols = Object.keys(sHeaders)

    if (tSheet) {
      const tMerges = collectMerges(tSheet)
      const tHeaderRow = detectHeaderRow(tSheet, tMerges.slaves)
      const tHeaders = extractHeaders(tSheet, tHeaderRow, tMerges.slaves)
      const tCols = Object.keys(tHeaders)

      const allCols = new Set<string>([...sCols, ...tCols])
      totalHeaders += allCols.size
      allCols.forEach((col) => {
        if (isHeaderSimilar(sHeaders[col] || '', tHeaders[col] || '')) {
          totalMatched++
        }
      })
    } else {
      // 模板没这页 → 该 sheet 全列算未匹配
      totalHeaders += sCols.length
    }

    // 填充计数（用于 reversed 检测）
    sampleFilled += countFilledCells(sSheet)
    templateFilled += countFilledCells(tSheet)
  }

  const matchPercent =
    totalHeaders > 0 ? Math.round((totalMatched / totalHeaders) * 100) : 0
  // 模板填得比样例满 50% 以上 → 怀疑反了。要求样例至少 1 cell（避免空文件误判）
  const reversed = sampleFilled > 0 && templateFilled > sampleFilled * 1.5

  return {
    matchPercent,
    matchedHeaders: totalMatched,
    totalHeaders,
    reversed,
    templateFilledCount: templateFilled,
    sampleFilledCount: sampleFilled
  }
}

/**
 * 计算模板与样例的差异。
 *
 * 对齐策略：
 *   - 工作表按 **位置** 对齐（template.worksheets[i] vs sample.worksheets[i]），
 *     而不是按名字 —— 用户改名是常见操作
 *   - 单元格按 **ref** 对齐（A1↔A1）
 *   - 任一边出错（解析失败 / 不是 xlsx）→ 抛错，调用方 catch 后降级走"无样例"流程
 */
export async function computeExcelDiff(
  templateBuf: ArrayBuffer,
  sampleBuf: ArrayBuffer,
  templateName: string,
  sampleName: string
): Promise<ExcelDiff> {
  // 重要顺序：先扫样例 drawing 找图片 cell，再 loadWorkbook（loadWorkbook 内部会
  // 剥 drawings，剥完就找不回来了）。
  const sampleImageCells = await detectImageCells(sampleBuf)

  const [templateWb, sampleWb] = await Promise.all([
    loadWorkbook(templateBuf),
    loadWorkbook(sampleBuf)
  ])

  const filledByExample: ExcelDiffFilledCell[] = []
  const imageCells: ExcelDiffImageCell[] = []
  const differing: ExcelDiffDifferingCell[] = []
  const sheets: string[] = []
  const dims: Record<string, ExcelDiffSheetDims> = {}
  const headerRows: Record<string, number> = {}
  const headers: Record<string, Record<string, string>> = {}

  const templateSheets = templateWb.worksheets
  const sampleSheets = sampleWb.worksheets

  // sheet 配对：先按 name 同名匹配，没匹配上的再按位置补齐。
  // 避免用户调整过 sheet 顺序时一一错位（明明是同 sheet 却 diff 出大量假差异）。
  const tSheetByName = new Map<string, ExcelJS.Worksheet>()
  templateSheets.forEach((s) => tSheetByName.set(s.name, s))

  for (let si = 0; si < sampleSheets.length; si++) {
    const sSheet = sampleSheets[si]
    if (!sSheet) continue
    // 优先 name 匹配，name 未命中再 fallback 到位置
    const tSheet = tSheetByName.get(sSheet.name) || templateSheets[si]

    const sheetName = sSheet.name
    sheets.push(sheetName)
    dims[sheetName] = {
      rows: sSheet.rowCount,
      cols: sSheet.columnCount
    }

    // 先收合并区域：cell-scan 跳过 slaves，differing 把 master 标上 mergedRange
    const { slaves: mergedSlaves, rangeOfMaster } = collectMerges(sSheet)
    // 自动找真表头行（绕开顶部合并标题行）
    const headerRow = detectHeaderRow(sSheet, mergedSlaves)
    headerRows[sheetName] = headerRow
    headers[sheetName] = extractHeaders(sSheet, headerRow, mergedSlaves)

    const maxRow = Math.max(tSheet?.rowCount || 0, sSheet.rowCount)
    const maxCol = Math.max(tSheet?.columnCount || 0, sSheet.columnCount)
    const sheetImageRefs = sampleImageCells.get(si) || new Set<string>()

    // 先把"样例上盖了图片"的 cell 列出来（与 filledByExample / differing 互斥，
    // 优先级最高——agent 看到这一类必须用图片插入工具，不能当文本处理）
    sheetImageRefs.forEach((ref) => {
      const colLetter = ref.replace(/\d+$/, '')
      const rowMatch = ref.match(/\d+$/)
      const r = rowMatch ? parseInt(rowMatch[0], 10) : 0
      const placeholderText =
        r > 0 ? cellToText(sSheet.getCell(`${colLetter}${r}`)) : ''
      imageCells.push({
        sheet: sheetName,
        ref,
        header: pickHeader(sSheet, colLetter, headerRow),
        placeholderText: placeholderText || undefined
      })
    })

    for (let r = 1; r <= maxRow; r++) {
      for (let c = 1; c <= maxCol; c++) {
        const colLetter = numberToColLetter(c)
        const ref = `${colLetter}${r}`
        // 跳过：图片 cell（已计入 imageCells）/ 合并区域 slave（防 26x 同值刷屏）
        if (sheetImageRefs.has(ref)) continue
        if (mergedSlaves.has(ref)) continue
        // 跳过表头本身（表头已经在 headers map 里，不重复列）
        if (r === headerRow) continue

        const sCell = sSheet.getCell(r, c)
        const sText = cellToText(sCell)
        // 样例 cell 为空 / 是占位文本（"请输入内容..." / "（待填）"）→ 视为空，
        // 否则 agent 会把占位当真值学着填，是真实出现过的语义错误
        if (!sText || isPlaceholderText(sText)) continue

        const tCell = tSheet ? tSheet.getCell(r, c) : undefined
        const tText = cellToText(tCell)
        // 模板侧的占位也视为空 cell —— 这样模板 placeholder + 样例真实值 = "样例 fill 的字段"
        const tEffective = tText && !isPlaceholderText(tText) ? tText : ''

        if (!tEffective) {
          filledByExample.push({
            sheet: sheetName,
            ref,
            header: pickHeader(sSheet, colLetter, headerRow),
            value: truncateValue(sText),
            mergedRange: rangeOfMaster.get(ref)
          })
        } else if (tEffective !== sText) {
          differing.push({
            sheet: sheetName,
            ref,
            header: pickHeader(sSheet, colLetter, headerRow),
            templateValue: truncateValue(tEffective),
            sampleValue: truncateValue(sText),
            mergedRange: rangeOfMaster.get(ref)
          })
        }
      }
    }
  }

  return {
    templateName,
    sampleName,
    filledByExample,
    imageCells,
    differing,
    sheets,
    dims,
    headerRows,
    headers
  }
}

/** 1 → A, 27 → AA。ExcelJS getColumn().letter 在某些版本不稳，给个 fallback。 */
function numberToColLetter(n: number): string {
  let s = ''
  let x = n
  while (x > 0) {
    const m = (x - 1) % 26
    s = String.fromCharCode(65 + m) + s
    x = Math.floor((x - 1) / 26)
  }
  return s
}

/** A → 1, AA → 27。 */
function colLetterToNumber(letter: string): number {
  let n = 0
  for (let i = 0; i < letter.length; i++) {
    n = n * 26 + (letter.charCodeAt(i) - 64)
  }
  return n
}

/**
 * 把一堆 cell ref（如 ["A4","A5","A6","A8","A9"]）压成 ranges
 * （["A4:A6","A8:A9"]）。仅折叠"同列连续行"的 run，跨列不折叠。
 */
function collapseContiguousRefs(refs: string[]): string[] {
  type Parsed = { col: string; row: number; ref: string }
  const parsed: Parsed[] = []
  for (const ref of refs) {
    const m = ref.match(/^([A-Z]+)(\d+)$/)
    if (!m) continue
    parsed.push({ col: m[1], row: parseInt(m[2], 10), ref })
  }
  parsed.sort((a, b) =>
    a.col === b.col ? a.row - b.row : a.col < b.col ? -1 : 1
  )
  const out: string[] = []
  let runStart: Parsed | null = null
  let runEnd: Parsed | null = null
  const flush = () => {
    if (!runStart || !runEnd) return
    if (runStart.ref === runEnd.ref) out.push(runStart.ref)
    else out.push(`${runStart.ref}:${runEnd.ref}`)
    runStart = runEnd = null
  }
  for (const p of parsed) {
    if (
      runStart &&
      runEnd &&
      runStart.col === p.col &&
      p.row === runEnd.row + 1
    ) {
      runEnd = p
    } else {
      flush()
      runStart = p
      runEnd = p
    }
  }
  flush()
  return out
}

/**
 * 把 diff 序列化成首条消息里的引文文本（中文，给 LLM 读）。
 *
 * 紧凑布局（相比每条字段一行的旧版可节省 60-80% token）：
 *   1. 表头在顶部统一列一次（A=拍摄人 | B=拍摄时间 | …），后文不再重复
 *   2. 样例数据按 row 聚合，一行一条 r4: B=王二麻子 | D=2026-05-07 18:52:01 | …
 *   3. 图片 cell 用 ranges 折叠（A4:A19 而不是 16 行）
 *   4. 合并区域差异用 mergedRange 合并成一条（A1:Z1: 模板 X → 样例 Y）
 *   5. 合并区域 slave / 表头行 / 图片 cell 不重复出现在 filledByExample
 *
 * 样例文件以**附件**形式同时给 agent，此 prompt 是浓缩字段映射证据 —— 优先看，
 * 看不清再 read 附件原文件。
 */
export function diffToPromptText(diff: ExcelDiff): string {
  const lines: string[] = []
  lines.push('【参考样例的字段映射（前端本地解析提取）】')
  lines.push(
    `样例文件「${diff.sampleName}」以附件形式同时发给你了。下面是浓缩的字段映射，优先按这段填模板；个别字段语义不清再 read 附件原文件。`
  )

  for (const sheetName of diff.sheets) {
    const dims = diff.dims[sheetName] || { rows: 0, cols: 0 }
    const headerRow = diff.headerRows[sheetName]
    const headerMap = diff.headers[sheetName] || {}

    lines.push('')
    lines.push(`### Sheet「${sheetName}」(${dims.rows} 行 × ${dims.cols} 列)`)

    // 1) 表头：A=照片/视频 | B=拍摄人 | C=水印名称 | …
    const headerEntries = Object.entries(headerMap)
    if (headerEntries.length > 0) {
      headerEntries.sort(([a], [b]) =>
        a.length !== b.length ? a.length - b.length : a < b ? -1 : 1
      )
      const headerStr = headerEntries
        .map(([col, t]) => `${col}=${t}`)
        .join(' | ')
      lines.push(`表头 (r${headerRow}): ${headerStr}`)
    }

    // 2) 图片 cell：按所在列分组，加表头注释
    const sheetImages = diff.imageCells.filter((c) => c.sheet === sheetName)
    if (sheetImages.length > 0) {
      // 按 col letter 聚合：A 列下属 A4-A19 一行，B 列下属 ... 另一行
      const byCol = new Map<string, string[]>()
      sheetImages.forEach((c) => {
        const col = c.ref.replace(/\d+$/, '')
        if (!byCol.has(col)) byCol.set(col, [])
        byCol.get(col)!.push(c.ref)
      })
      const placeholder = sheetImages[0]?.placeholderText
        ? `（cell value "${sheetImages[0].placeholderText}" 是占位文本，**别照着写**）`
        : ''
      lines.push(
        `🖼️ 嵌入图片单元格（必须用图片插入能力填，不能直接写 cell value）${placeholder}：`
      )
      const colKeys = Array.from(byCol.keys()).sort()
      for (const col of colKeys) {
        const refs = byCol.get(col)!
        const ranges = collapseContiguousRefs(refs)
        const headerText = headerMap[col] ? `(${headerMap[col]})` : ''
        lines.push(
          `- ${col} 列${headerText}：${ranges.join(', ')} 共 ${refs.length} 个`
        )
      }
    }

    // 3) 样例数据 —— 按 row 聚合 + 按"列填充签名"分组，每组按 3 档规则处理：
    //   case A 无合并 cell：5 行采样（首 4 + 末 1 + 中间折叠），适合数据型表
    //   case B 有合并 cell ≤ 20 行：全量 + 合并标注，适合常规表单
    //   case C 有合并 cell > 20 行：放弃 prompt 内展开，提示 agent read 附件
    const sheetFilled = diff.filledByExample.filter(
      (c) => c.sheet === sheetName
    )
    if (sheetFilled.length > 0) {
      const MAX_SAMPLES_PER_GROUP = 5
      const FORM_FULL_LIMIT = 20 // 含合并单元格组的全量上限

      type RowCell = { col: string; value: string; mergedRange?: string }
      const byRow = new Map<number, RowCell[]>()
      for (const c of sheetFilled) {
        const m = c.ref.match(/^([A-Z]+)(\d+)$/)
        if (!m) continue
        const col = m[1]
        const row = parseInt(m[2], 10)
        if (!byRow.has(row)) byRow.set(row, [])
        byRow.get(row)!.push({
          col,
          value: c.value,
          mergedRange: c.mergedRange
        })
      }
      const totalRows = byRow.size

      // 列字母排序：先按字母长度（A < AA < AAA），再按字典序
      const sortCols = (cells: RowCell[]): RowCell[] =>
        [...cells].sort((a, b) =>
          a.col.length !== b.col.length
            ? a.col.length - b.col.length
            : a.col < b.col
              ? -1
              : 1
        )

      // 结构签名 = 排好序的非空列字母集合
      const sigOf = (cells: RowCell[]): string =>
        sortCols(cells)
          .map((c) => c.col)
          .join(',')

      // 按签名分组
      const sigGroups = new Map<
        string,
        Array<{ row: number; cells: RowCell[] }>
      >()
      byRow.forEach((cells, row) => {
        const sig = sigOf(cells)
        if (!sigGroups.has(sig)) sigGroups.set(sig, [])
        sigGroups.get(sig)!.push({ row, cells })
      })
      const groups = Array.from(sigGroups.values())
        .map((rows) => rows.sort((a, b) => a.row - b.row))
        .sort((a, b) =>
          a.length !== b.length ? b.length - a.length : a[0].row - b[0].row
        )

      // ─ 列分布总结：每列高频值（≥40% 占比）单独列出，让 agent 知道"该列大概率
      //   是 X" 这种语义证据；后续行级 sample 仍然给具体值，互补不冲突。
      //   只在数据量足够（>= 3 行）时才生成总结，否则没统计意义。
      if (totalRows >= 3) {
        const colFreq = new Map<string, Map<string, number>>()
        byRow.forEach((cells) => {
          for (const c of cells) {
            if (!colFreq.has(c.col)) colFreq.set(c.col, new Map())
            const freq = colFreq.get(c.col)!
            freq.set(c.value, (freq.get(c.value) || 0) + 1)
          }
        })
        const distLines: string[] = []
        const sortedCols = Array.from(colFreq.keys()).sort((a, b) =>
          a.length !== b.length ? a.length - b.length : a < b ? -1 : 1
        )
        for (const col of sortedCols) {
          const freq = colFreq.get(col)!
          const total = Array.from(freq.values()).reduce((s, n) => s + n, 0)
          if (total < 3) continue // 只列 ≥3 次的列
          // 排行：值出现次数 ≥ total*40% 算高频
          const ranked = Array.from(freq.entries()).sort((a, b) => b[1] - a[1])
          const highFreq = ranked.filter(([, n]) => n / total >= 0.4)
          if (highFreq.length === 0) continue
          const headerText = headerMap[col] ? ` (${headerMap[col]})` : ''
          const items = highFreq
            .slice(0, 3)
            .map(
              ([v, n]) => `"${v.length > 30 ? v.slice(0, 30) + '…' : v}"×${n}`
            )
            .join(', ')
          const otherCount = total - highFreq.reduce((s, [, n]) => s + n, 0)
          const tail = otherCount > 0 ? ` + 其它 ${otherCount} 个` : ''
          distLines.push(`- ${col}${headerText}: ${items}${tail}`)
        }
        if (distLines.length > 0) {
          lines.push('列高频值分布（≥40% 占比）：')
          lines.push(...distLines)
        }
      }

      lines.push(
        `样例数据行（共 ${totalRows} 行 / ${groups.length} 种结构，按列含义填到模板对应位置）：`
      )

      // formatRow 支持合并标注：A(合并 A4:A12)=1
      const formatRow = (row: number, cells: RowCell[]): string => {
        const cellStrs = sortCols(cells).map(({ col, value, mergedRange }) =>
          mergedRange
            ? `${col}(合并 ${mergedRange})=${value}`
            : `${col}=${value}`
        )
        return `r${row}: ${cellStrs.join(' | ')}`
      }

      // 该 group 任一行任一 cell 有合并 → 走 case B/C
      const hasMergedCell = (
        group: Array<{ row: number; cells: RowCell[] }>
      ): boolean => group.some((g) => g.cells.some((c) => !!c.mergedRange))

      for (const group of groups) {
        const merged = hasMergedCell(group)

        // ─ case C: 含合并 + 行数过多 → 不展开，让 agent read 附件 ─
        if (merged && group.length > FORM_FULL_LIMIT) {
          const fromRow = group[0].row
          const toRow = group[group.length - 1].row
          lines.push(
            `⚠️ 复杂合并结构组：${group.length} 行（r${fromRow}-r${toRow}），含合并单元格、结构无法在 prompt 内紧凑表达。请用 read 工具查看本消息的"参考样例"附件「${diff.sampleName}」，重点看 r${fromRow}-r${toRow}。`
          )
          continue
        }

        // ─ case B: 含合并 + 行数 ≤ 20 → 全量输出（合并标注已在 formatRow 处理）─
        if (merged) {
          for (const rep of group) {
            lines.push(formatRow(rep.row, rep.cells))
          }
          continue
        }

        // ─ case A: 无合并 → 5 行采样（首 4 + 末 1 + 中间折叠）─
        if (group.length <= MAX_SAMPLES_PER_GROUP) {
          for (const rep of group) {
            lines.push(formatRow(rep.row, rep.cells))
          }
          continue
        }
        const head = group.slice(0, MAX_SAMPLES_PER_GROUP - 1)
        const tail = group[group.length - 1]
        const omitted = group.length - MAX_SAMPLES_PER_GROUP
        for (const rep of head) {
          lines.push(formatRow(rep.row, rep.cells))
        }
        if (omitted > 0) {
          lines.push(
            `…（中间 ${omitted} 行结构相同，分布于 r${
              head[head.length - 1].row + 1
            }-r${tail.row - 1}）`
          )
        }
        lines.push(formatRow(tail.row, tail.cells))
      }
    }

    // 4) 差异 —— 合并区域用 mergedRange 折成一行
    const sheetDiffering = diff.differing.filter((c) => c.sheet === sheetName)
    if (sheetDiffering.length > 0) {
      lines.push(`合并/标题区差异（仅参考，最终以模板为准）：`)
      for (const c of sheetDiffering.slice(0, 20)) {
        const refLabel = c.mergedRange || c.ref
        const headerPart = c.header ? `（${c.header}）` : ''
        lines.push(
          `- ${refLabel}${headerPart}: 模板"${c.templateValue}" → 样例"${c.sampleValue}"`
        )
      }
      if (sheetDiffering.length > 20) {
        lines.push(`- …还有 ${sheetDiffering.length - 20} 处差异未列出`)
      }
    }
  }

  return lines.join('\n')
}

/** 读 File → ArrayBuffer */
export function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const fr = new FileReader()
    fr.onload = () => resolve(fr.result as ArrayBuffer)
    fr.onerror = () => reject(fr.error || new Error('FileReader 读取失败'))
    fr.readAsArrayBuffer(file)
  })
}
